import { ComponentProps, forwardRef } from 'react';
import clsx from 'clsx';
import type { InputNumberRef } from '@rc-component/input-number';

import { OpInputNumber } from 'new-components/DLS/OpInputNumber/OpInputNumber';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { useOpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';

interface OpFormInputNumberProps
  extends Pick<
      ComponentProps<typeof OpForm.Item>,
      | 'name'
      | 'label'
      | 'rules'
      | 'tooltip'
      | 'validateTrigger'
      | 'validateStatus' // Temp: For SUIR wrapped version
      | 'help' // Temp: For SUIR wrapped version
      | 'required' // Temp: For SUIR wrapped version
    >,
    Omit<ComponentProps<typeof OpInputNumber>, 'name' | 'required'> {
  formItemClassName?: string;
  isLoading?: boolean;
}

export const OpFormInputNumber = forwardRef<
  InputNumberRef,
  OpFormInputNumberProps
>(
  (
    {
      // Form Item props
      formItemClassName,
      name,
      label,
      rules,
      tooltip,
      validateTrigger,
      required, // TEMP: For old form component validation
      validateStatus, // TEMP: For old form component validation
      help, // TEMP: For old form component validation

      // For skeleton loading state
      isLoading,

      // Wrapped element props
      disabled,

      testId,

      ...elementProps
    },
    ref,
  ) => {
    /** The Form is wrapped in a provider that passes the loading and readOnly
     * contexts to the component */
    const { isDataLoading, isReadOnly } = useOpFormContext();

    return (
      <OpForm.Item
        className={clsx('op-form-input', formItemClassName)}
        name={name}
        label={label}
        rules={rules}
        tooltip={tooltip}
        validateTrigger={validateTrigger}
        required={required} // TEMP: For old form component validation
        validateStatus={validateStatus} // TEMP: For old form component validation
        help={help} // TEMP: For old form component validation
        testId={testId ? `${testId}-op-form-item` : undefined}
      >
        {isDataLoading || isLoading ? (
          /** A placeholder in the shape of the component
           * so we can immediately render the structure of the form while
           * data is loading */
          <OpSkeleton.Input active block />
        ) : (
          /** The actual component that is rendered when we have the data */
          <OpInputNumber
            disabled={isReadOnly || disabled}
            testId={testId}
            {...elementProps}
            ref={ref}
          />
        )}
      </OpForm.Item>
    );
  },
);
